<template>
  <table class="govuk-table fds-sortable-table">
    <caption class="govuk-visually-hidden">
      {{tableCaption}} (column headers with buttons are sortable).
    </caption>
    <thead
      v-if="hasColumnHeadings"
      class="govuk-table__head"
    >
      <first-row-view
        :row="firstRow"
        :all-headings="true"
        :action-heading="actionHeading"
      />
    </thead>
    <tbody class="govuk-table__body">
      <first-row-view
        v-if="!hasColumnHeadings"
        :row="firstRow"
        :all-headings="false"
        :action-heading="actionHeading"
      />
      <tr
        v-for="row in rowsInTableBody"
        :key="row"
        class="govuk-table__row"
      >
        <table-value-view
          :table-value="row.rowValues[0]"
          :show-as-heading="hasRowHeadings"
          :sortable="false"
          :top-header="false"
          :index="null"
        />

        <table-value-view
          v-for="value in row.rowValues.slice(1)"
          :key="value"
          :table-value="value"
          :show-as-heading="false"
          :top-header="false"
          :sortable="false"
          :index="null"
        />

        <action-links
          v-if="actionHeading != null"
          :row="row"
        />
      </tr>
    </tbody>
  </table>
</template>

<script setup>
// eslint-disable-next-line import/no-extraneous-dependencies
import { computed, toRefs } from "vue";
import ActionLinks from "./ActionLinks.vue";
import FirstRowView from "./FirstRowView.vue";
import TableValueView from "./TableValueView.vue";
import sortRows from "../sorting";
import { useTableStore } from "../tableStore";

const tableContents = defineProps({
  /** @type {TableContents} */
  tableContent: Object,
  tableId: String,
  tableCaption: String,
});

const {
  /** @type {Ref<TableRow[]>} */
  tableRows,

  /** @type {Ref<TableHeadingStyle>} */
  tableHeadingStyle,

  /** @type {string} optional string to define column heading when actions present */
  actionHeading,

  /** @type {Ref<number>} */
  defaultSortIndex,

  /** @type {Ref<number>} */
  defaultSortDirection,
} = toRefs(tableContents.tableContent);

const hasRowHeadings = computed(() => tableHeadingStyle.value === "ROW" || tableHeadingStyle.value === "ROW_AND_COLUMN");
const hasColumnHeadings = computed(() => tableHeadingStyle.value === "COLUMN" || tableHeadingStyle.value === "ROW_AND_COLUMN");

const tableStore = useTableStore();
if (tableContents.tableId != null) {
  tableStore.init(tableContents.tableId);
}

/** @type {ComputedRef<TableRow[]>} */
const sortedRows = computed(() => Array.from(hasColumnHeadings.value ? tableRows.value.slice(1) : tableRows.value)
  .sort((a, b) => sortRows(a, b, defaultSortIndex.value, defaultSortDirection.value, tableStore.sortedIndex, tableStore.sortDirection)));

// if only using row headings, sorted rows contains the initial row used in thead
const rowsInTableBody = computed(() => (hasColumnHeadings.value ? sortedRows.value : sortedRows.value.slice(1)));
const firstRow = computed(() => (hasColumnHeadings.value ? tableRows.value[0] : sortedRows.value[0]));

</script>